import React, { Fragment, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Table, Column, Cell } from "rsuite-table";
import "rsuite-table/dist/css/rsuite-table.css";
import "../../../css/rstable-overwrites.scss";
import CustomHeaderCell from "../../components/reports/CustomHeader";
import {
  Pagination,
  ReportControls,
  SalesSourceSelect,
  TenderTypeSelect,
} from "../../components/reports";
import {
  CurrencyRenderer,
  TextInputFilter,
} from "../../components/reports/CellTypes";
import { API_STATE } from "../../constants/api";
import { ReportsMenuStructure } from "../../menuStructures";
import { useReportsDetails } from "../../hooks/reportsHooks";
import { useStoreDetails } from "../../hooks/storeHooks";
import { useShowError } from "../../hooks/errorHooks";
import { useFetchSalesByOrder } from "../../hooks/reports/useFetchSalesByOrder";
import SetActiveMenu from "../../menuStructures/SetActiveMenu";
import {
  salesByOrderCSVHeaders1,
  salesByOrderCSVHeaders2,
} from "../../constants/reportCSVHeaders";
import { rowPerPageOptions } from "../../constants/reports";
import { addLocalizedDate } from "../../transformers/helpers/addLocalizedDate";
import { parseSearchParams } from "../../helpers/reportHelpers";

function SalesByTender() {
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search).get("filters");
  const parsedSearchParams = parseSearchParams(searchParams);
  const sourcesParam = parsedSearchParams?.sources
    ? parsedSearchParams.sources
    : {};
  const tenderTypesParam = parsedSearchParams?.tenderTypes
    ? parsedSearchParams.tenderTypes
    : {};
  const {
    apiState,
    currentPage,
    filterValues,
    overrideTableHeight,
    rowsPerPage,
    sortColumn,
    tableData,
    computedTableData,
    reset,
    setApiState,
    setCurrentPage,
    setTableRowsPerPage,
    setFilters,
    setSortingColumn,
    setSourceDataDetails,
    updateFilterValue,
  } = useReportsDetails();
  const tableRef = useRef<HTMLElement>();
  const modalRef = useRef<HTMLElement>();
  const { currencyCode } = useStoreDetails();
  const showError = useShowError();
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [timeZone, setTimeZone] = useState<string>(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [showFilterTenderModal, setShowFilterTenderModal] = useState(false);
  const [showFilterSourceModal, setShowFilterSourceModal] = useState(false);
  const [filterTenders, setFilterTenders] = useState({});
  const [filterSources, setFilterSources] = useState({});
  const [tenderTypeNames, setTenderTypeNames] = useState([]);
  const [sourceTypeNames, setSourceTypeNames] = useState([]);

  const {
    data: sourceData,
    isFetching,
    error,
  } = useFetchSalesByOrder(
    { startDate, endDate },
    { enabled: !!startDate && !!endDate, initialData: undefined }
  );

  useEffect(() => {
    document.title = "Reports | BinderPOS";
    reset();
    setFilters(filters);
  }, []);

  useEffect(() => {
    if (isFetching) {
      setApiState(API_STATE.LOADING);
    } else if (error) {
      showError(
        error as any,
        "Failed to load report",
        "There was an error retrieving your sales by tender report. Please try again"
      );
      setApiState(API_STATE.ERROR);
    } else if (sourceData) {
      onQuerySuccessHandler(sourceData);
      setApiState(API_STATE.SUCCESS);
    }
  }, [isFetching, sourceData, error]);

  useEffect(() => {
    if (showFilterSourceModal || showFilterTenderModal) {
      modalRef.current?.focus();
    } else {
      tableRef.current?.focus();
    }
  }, [showFilterSourceModal, showFilterTenderModal]);

  const filters = [
    {
      name: "orderId",
      condition: (filterValues) => filterValues?.orderId,
      filterFunction: (row, filterValues) =>
        row.orderId?.includes(filterValues?.orderId),
    },
    {
      name: "tender type",
      condition: () => true,
      filterFunction: (row, filterValues) => {
        if (
          Object.values(filterValues?.tenderTypes || {}).filter(
            (enabled) => enabled
          ).length === tenderTypeNames.length
        ) {
          // No filtering in use, return all rows
          return row;
        }
        return !!row?.tenders?.find(
          (tender) => filterValues?.tenderTypes?.[tender.type]
        );
      },
    },
    {
      name: "source",
      condition: () => true,
      filterFunction: (row, filterValues) =>
        row.source && filterValues?.sources?.[row.source],
    },
  ];

  const updateFilterTenders = (tenders) => {
    setFilterTenders(tenders);
    updateFilterValue("tenderTypes", tenders);
  };

  const updateFilterSources = (sources) => {
    setFilterSources(sources);
    updateFilterValue("sources", sources);
  };

  const onQuerySuccessHandler = (data) => {
    const { rows, tenderTypes, sources } = data;
    setTenderTypeNames(tenderTypes);
    setSourceTypeNames(sources);
    const selectedTenders = tenderTypes.reduce((selected, tender) => {
      if (tenderTypesParam[tender] !== undefined) {
        selected[tender] = tenderTypesParam[tender];
      } else {
        selected[tender] = true;
      }
      return selected;
    }, {});
    const selectedSources = sources.reduce((selected, source) => {
      if (sourcesParam[source] !== undefined) {
        selected[source] = sourcesParam[source];
      } else {
        selected[source] = true;
      }
      return selected;
    }, {});
    updateFilterTenders(selectedTenders);
    updateFilterSources(selectedSources);
    setSourceDataDetails(rows);
  };

  const toggleFilterTenders = (selected) => {
    updateFilterTenders(selected);
    setShowFilterTenderModal(false);
  };

  const toggleFilterSources = (selected) => {
    updateFilterSources(selected);
    setShowFilterSourceModal(false);
  };

  const handleSetDateRange = (fromDate, toDate, tz) => {
    setTimeZone(tz);
    setStartDate(fromDate);
    setEndDate(toDate);
  };

  const getTenderTotal = (tableData, key) => {
    const tenderType = key.split("_^")?.pop();
    if (!tenderType) return;
    return (
      tableData.length &&
      tableData.reduce(
        (sum, row) =>
          sum +
          ((row.tenders &&
            row.tenders.find((tender) => tender.type === tenderType)?.amount) ||
            0),
        0
      )
    );
  };

  const csvFilename = "Sales By Tender.csv";

  const csvHeadersWithTenders = tenderTypeNames.reduce(
    (arr, tender) => [
      ...arr,
      {
        name: tender,
        key: tender,
        formatter: (number) => number && number.toFixed(2),
      },
    ],
    salesByOrderCSVHeaders1
  );
  const csvHeaders = [...csvHeadersWithTenders, ...salesByOrderCSVHeaders2];

  const data = addLocalizedDate(computedTableData(), timeZone, "closedAt");

  return (
    <Fragment>
      <SetActiveMenu menuStructure={ReportsMenuStructure} />
      <div className="app-header">
        <h1 className="title">
          <span className="header-text">Sales By Tender</span>
        </h1>
      </div>
      <div className="report">
        <ReportControls
          setDateRange={handleSetDateRange}
          csvHeaders={csvHeaders}
          csvFilename={csvFilename}
          hidePresets
          maxRangeMonths={2}
        />
        {apiState !== API_STATE.INITIAL && (
          <div className="data-table">
            <div className="table-container">
              <Table
                data={data}
                headerHeight={80}
                renderEmpty={() => <div>No data to display</div>}
                loading={apiState == API_STATE.LOADING}
                onSortColumn={setSortingColumn}
                sortColumn={sortColumn.key}
                sortType={sortColumn.order}
                className="rs-table--multi-header"
                height={overrideTableHeight}
                fillHeight={overrideTableHeight === 0}
              >
                <Column key="col_date" width={200} sortable resizable>
                  <CustomHeaderCell title="Date" />
                  <Cell dataKey="closedAt">
                    {(rowData) => rowData.closedAt_localized}
                  </Cell>
                </Column>
                <Column key="col_order_id" width={130} sortable resizable>
                  <CustomHeaderCell title="Order Id">
                    <TextInputFilter
                      aria-label="Filter by order ID"
                      title="Order id"
                      value={filterValues?.orderId || ""}
                      setValue={(value) => updateFilterValue("orderId", value)}
                    />
                  </CustomHeaderCell>
                  <Cell dataKey="orderId" />
                </Column>
                <Column
                  key="col_source"
                  width={120}
                  align="right"
                  sortable
                  resizable
                >
                  <CustomHeaderCell title="Source">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={(event) => {
                        tableRef.current = event.currentTarget;
                        setShowFilterSourceModal(true);
                      }}
                    >
                      Sources
                    </button>
                  </CustomHeaderCell>
                  <Cell dataKey="source">
                    {(rowData) => rowData?.source?.replace(/_/g, " ")}
                  </Cell>
                </Column>
                <Column
                  key="col_lineCount"
                  width={120}
                  align="right"
                  sortable
                  resizable
                >
                  <CustomHeaderCell title="Tender types">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={(event) => {
                        tableRef.current = event.currentTarget;
                        setShowFilterTenderModal(true);
                      }}
                    >
                      Tender Types
                    </button>
                  </CustomHeaderCell>
                  <Cell dataKey="lineCount" />
                </Column>
                {Object.keys(filterTenders)
                  .filter((type) => filterTenders[type])
                  .map((tenderType, index) => (
                    <Column
                      key={`col_tender_${index}_^${tenderType}`}
                      width={110}
                      align="right"
                      sortable
                      resizable
                    >
                      <CustomHeaderCell title={tenderType}>
                        {CurrencyRenderer({
                          currency: currencyCode,
                          cellData: getTenderTotal(tableData, tenderType),
                          style: { paddingRight: "8px" },
                        })}
                      </CustomHeaderCell>
                      <Cell dataKey={tenderType}>
                        {(rowData) =>
                          CurrencyRenderer({
                            cellData: rowData[tenderType],
                            currency: currencyCode,
                            style: { paddingRight: "8px" },
                          })
                        }
                      </Cell>
                    </Column>
                  ))}
                <Column
                  key="col_subtotal"
                  width={100}
                  align="right"
                  sortable
                  resizable
                >
                  <CustomHeaderCell title="SubTotal">
                    {CurrencyRenderer({
                      currency: currencyCode,
                      cellData: tableData?.reduce(
                        (sum, row) => sum + row.subTotal,
                        0
                      ),
                      style: { paddingRight: "8px" },
                    })}
                  </CustomHeaderCell>
                  <Cell dataKey="subTotal">
                    {(rowData) =>
                      CurrencyRenderer({
                        cellData: rowData.subTotal,
                        currency: currencyCode,
                        style: { paddingRight: "8px" },
                      })
                    }
                  </Cell>
                </Column>
                <Column
                  key="col_shipping"
                  width={100}
                  align="right"
                  sortable
                  resizable
                >
                  <CustomHeaderCell title="Shipping">
                    {CurrencyRenderer({
                      currency: currencyCode,
                      cellData: tableData?.reduce(
                        (sum, row) => sum + row.shipping,
                        0
                      ),
                      style: { paddingRight: "8px" },
                    })}
                  </CustomHeaderCell>
                  <Cell dataKey="shipping">
                    {(rowData) =>
                      CurrencyRenderer({
                        cellData: rowData.shipping,
                        currency: currencyCode,
                        style: { paddingRight: "8px" },
                      })
                    }
                  </Cell>
                </Column>
                <Column
                  key="col_discount"
                  width={100}
                  align="right"
                  sortable
                  resizable
                >
                  <CustomHeaderCell title="Discount">
                    {CurrencyRenderer({
                      currency: currencyCode,
                      cellData: tableData?.reduce(
                        (sum, row) => sum + row.discount,
                        0
                      ),
                      style: { paddingRight: "8px" },
                    })}
                  </CustomHeaderCell>
                  <Cell dataKey="discount">
                    {(rowData) =>
                      CurrencyRenderer({
                        cellData: rowData.discount,
                        currency: currencyCode,
                        style: { paddingRight: "8px" },
                      })
                    }
                  </Cell>
                </Column>
                <Column
                  key="col_tax"
                  width={100}
                  align="right"
                  sortable
                  resizable
                >
                  <CustomHeaderCell title="Tax">
                    {CurrencyRenderer({
                      currency: currencyCode,
                      cellData: tableData?.reduce(
                        (sum, row) => sum + row.tax,
                        0
                      ),
                      style: { paddingRight: "8px" },
                    })}
                  </CustomHeaderCell>
                  <Cell dataKey="tax">
                    {(rowData) =>
                      CurrencyRenderer({
                        cellData: rowData.tax,
                        currency: currencyCode,
                        style: { paddingRight: "8px" },
                      })
                    }
                  </Cell>
                </Column>
                <Column
                  key="col_total"
                  width={100}
                  align="right"
                  sortable
                  resizable
                >
                  <CustomHeaderCell title="Total">
                    {CurrencyRenderer({
                      currency: currencyCode,
                      cellData: tableData?.reduce(
                        (sum, row) => sum + row.total,
                        0
                      ),
                      style: { paddingRight: "8px" },
                    })}
                  </CustomHeaderCell>
                  <Cell dataKey="total">
                    {(rowData) =>
                      CurrencyRenderer({
                        cellData: rowData.total,
                        currency: currencyCode,
                        style: { paddingRight: "8px" },
                      })
                    }
                  </Cell>
                </Column>
              </Table>
            </div>
            <Pagination
              apiState={apiState}
              currentPage={currentPage}
              totalRowCount={tableData.length}
              rowsPerPage={rowsPerPage}
              rowPerPageOptions={rowPerPageOptions}
              setCurrentPage={setCurrentPage}
              setRowsPerPage={setTableRowsPerPage}
            />
          </div>
        )}
      </div>
      {showFilterTenderModal && (
        <TenderTypeSelect
          options={tenderTypeNames}
          value={filterTenders}
          setValue={toggleFilterTenders}
          onClose={() => setShowFilterTenderModal(false)}
          ref={modalRef}
        />
      )}
      {showFilterSourceModal && (
        <SalesSourceSelect
          options={sourceTypeNames}
          value={filterSources}
          setValue={toggleFilterSources}
          onClose={() => setShowFilterSourceModal(false)}
          ref={modalRef}
        />
      )}
    </Fragment>
  );
}

export default SalesByTender;
